import { useState, useEffect } from "react";
import {
  Lock,
  Users,
  Mail,
  MessageSquare,
  BarChart3,
  Eye,
  ChevronDown,
  ChevronUp,
  FileText,
  TrendingUp,
  Send,
  CheckCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border-2 border-[#002B7F]/10 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-10 h-10 flex items-center justify-center ${color} text-white`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
        {label}
      </span>
    </div>
    <p className="text-3xl font-black text-[#002B7F]">{value}</p>
  </div>
);

const DataTable = ({ title, icon: Icon, data, columns, emptyMessage }) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="bg-white border-2 border-[#002B7F]/10 mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#002B7F]/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
          <span className="font-bold text-[#002B7F]">
            {title} ({data.length})
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {expanded && (
        <div className="overflow-x-auto">
          {data.length === 0 ? (
            <p className="p-5 text-slate-400 text-sm">{emptyMessage}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b border-[#002B7F]/10 bg-[#002B7F]/5">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-[#002B7F]"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={row.id || row._id || row.email || i}
                    className="border-b border-[#002B7F]/5 hover:bg-[#002B7F]/5"
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className="px-5 py-3 text-slate-600 max-w-xs truncate"
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : row[col.key] || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [sentEmails, setSentEmails] = useState([]);

  // Email Composer
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setAuthenticated(true);
        sessionStorage.setItem("admin_auth", "true");
      } else {
        setError("Invalid password. Please try again.");
      }
    } catch {
      setError("Connection error. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (sessionStorage.getItem("admin_auth") === "true") {
      setAuthenticated(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- check auth on mount only

  useEffect(() => {
    if (!authenticated) return;

    const fetchAll = async () => {
      try {
        const [statsRes, contactsRes, volunteersRes, newsletterRes, analyticsRes, sentRes] =
          await Promise.all([
            fetch(`${API_URL}/api/admin/stats`),
            fetch(`${API_URL}/api/contact`),
            fetch(`${API_URL}/api/volunteers`),
            fetch(`${API_URL}/api/admin/newsletter`),
            fetch(`${API_URL}/api/admin/analytics`),
            fetch(`${API_URL}/api/admin/sent-emails`),
          ]);
        setStats(await statsRes.json());
        setContacts(await contactsRes.json());
        setVolunteers(await volunteersRes.json());
        setNewsletter(await newsletterRes.json());
        setAnalytics(await analyticsRes.json());
        setSentEmails(await sentRes.json());
      } catch (err) {
        // Data fetch failed — dashboard shows stale data
        if (process.env.NODE_ENV !== "production") {
          console.warn("Admin dashboard data fetch failed:", err);
        }
      }
    };
    fetchAll();
  }, [authenticated]);

  const formatDate = (ts) => {
    if (!ts) return "—";
    try {
      return new Date(ts).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return ts;
    }
  };

  if (!authenticated) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#002B7F]/5 flex items-center justify-center">
        <div className="max-w-sm w-full mx-4">
          <div className="bg-white border-2 border-[#002B7F]/10 p-8" data-testid="admin-login">
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#002B7F] text-white mx-auto mb-4">
                <Lock className="w-7 h-7" />
              </div>
              <h1 className="text-xl font-bold text-[#002B7F]">
                Campaign Admin
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Enter your password to continue
              </p>
            </div>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                className="w-full px-4 py-3 border-2 border-[#002B7F]/10 text-sm mb-4 focus:outline-none focus:border-[#D4AF37]"
              />
              {error && (
                <p className="text-red-600 text-xs mb-3">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                className="w-full px-4 py-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all disabled:opacity-50"
              >
                {loading ? "..." : "Login"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#002B7F]/5" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black text-[#002B7F]">
              Campaign Dashboard
            </h1>
            <p className="text-slate-500 text-sm">
              Cooper for Nevada — Admin Panel
            </p>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem("admin_auth");
              setAuthenticated(false);
            }}
            data-testid="admin-logout-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-red-600 transition-colors"
          >
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={MessageSquare}
              label="Contact Submissions"
              value={stats.contacts}
              color="bg-[#002B7F]"
            />
            <StatCard
              icon={Users}
              label="Volunteers"
              value={stats.volunteers}
              color="bg-emerald-600"
            />
            <StatCard
              icon={Mail}
              label="Newsletter Subscribers"
              value={stats.newsletter}
              color="bg-[#D4AF37]"
            />
            <StatCard
              icon={Eye}
              label="Page Views"
              value={stats.page_views}
              color="bg-violet-600"
            />
          </div>
        )}

        {/* Email Composer */}
        <div className="bg-white border-2 border-[#002B7F]/10 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Send className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-bold text-[#002B7F]">
              Send Email as Info@CooperForNV.com
            </span>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!emailTo || !emailSubject || !emailMessage) return;
              setEmailSending(true);
              setEmailStatus(null);
              try {
                const res = await fetch(`${API_URL}/api/admin/send-email`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    to_email: emailTo,
                    subject: emailSubject,
                    message: emailMessage,
                  }),
                });
                if (res.ok) {
                  setEmailStatus("sent");
                  setEmailTo("");
                  setEmailSubject("");
                  setEmailMessage("");
                  // Refresh sent emails
                  const sentRes = await fetch(`${API_URL}/api/admin/sent-emails`);
                  setSentEmails(await sentRes.json());
                } else {
                  const err = await res.json();
                  setEmailStatus(err.detail || "Failed to send");
                }
              } catch {
                setEmailStatus("Connection error");
              }
              setEmailSending(false);
            }}
            className="space-y-3"
          >
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  To
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="recipient@example.com"
                  required
                  data-testid="email-to"
                  className="w-full px-4 py-2.5 border-2 border-[#002B7F]/10 text-sm focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  required
                  data-testid="email-subject"
                  className="w-full px-4 py-2.5 border-2 border-[#002B7F]/10 text-sm focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                Message
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={5}
                required
                data-testid="email-message"
                className="w-full px-4 py-2.5 border-2 border-[#002B7F]/10 text-sm focus:outline-none focus:border-[#D4AF37] resize-none"
              />
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">
                Sends from: Info@CooperForNV.com &middot; Replies go to your Gmail
              </p>
              <button
                type="submit"
                disabled={emailSending}
                data-testid="email-send-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#002B7F] hover:bg-[#001F5C] text-white font-bold uppercase tracking-widest text-xs transition-all disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {emailSending ? "Sending..." : "Send Email"}
              </button>
            </div>
            {emailStatus === "sent" && (
              <div className="flex items-center gap-2 text-emerald-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                Email sent successfully!
              </div>
            )}
            {emailStatus && emailStatus !== "sent" && (
              <p className="text-red-600 text-sm">{emailStatus}</p>
            )}
          </form>
        </div>

        {/* Sent Emails */}
        <DataTable
          title="Sent Emails"
          icon={Send}
          data={sentEmails}
          emptyMessage="No emails sent yet."
          columns={[
            { key: "to", label: "To" },
            { key: "subject", label: "Subject" },
            { key: "message", label: "Message" },
            {
              key: "timestamp",
              label: "Sent",
              render: (v) => formatDate(v),
            },
          ]}
        />

        {/* Analytics */}
        {analytics && (
          <div className="bg-white border-2 border-[#002B7F]/10 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-bold text-[#002B7F]">
                Top Pages by Views
              </span>
            </div>
            {analytics.per_page.length === 0 ? (
              <p className="text-slate-400 text-sm">
                No page view data yet. Views will appear as visitors browse the
                site.
              </p>
            ) : (
              <div className="space-y-2">
                {analytics.per_page.map((p) => {
                  const maxViews = analytics.per_page[0]?.views || 1;
                  const pct = Math.round((p.views / maxViews) * 100);
                  return (
                    <div key={p.page} className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 w-40 truncate">
                        {p.page}
                      </span>
                      <div className="flex-1 bg-[#002B7F]/5 h-6 relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-[#002B7F]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-[#002B7F] w-12 text-right">
                        {p.views}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Contact Submissions */}
        <DataTable
          title="Contact Submissions"
          icon={MessageSquare}
          data={contacts}
          emptyMessage="No contact submissions yet."
          columns={[
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "subject", label: "Subject" },
            { key: "message", label: "Message" },
            {
              key: "timestamp",
              label: "Date",
              render: (v) => formatDate(v),
            },
          ]}
        />

        {/* Volunteers */}
        <DataTable
          title="Volunteer Signups"
          icon={Users}
          data={volunteers}
          emptyMessage="No volunteer signups yet."
          columns={[
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "zip_code", label: "Zip" },
            {
              key: "interests",
              label: "Interests",
              render: (v) => (Array.isArray(v) ? v.join(", ") : v),
            },
            {
              key: "timestamp",
              label: "Date",
              render: (v) => formatDate(v),
            },
          ]}
        />

        {/* Newsletter */}
        <DataTable
          title="Newsletter Subscribers"
          icon={Mail}
          data={newsletter}
          emptyMessage="No newsletter subscribers yet."
          columns={[
            { key: "email", label: "Email" },
            {
              key: "timestamp",
              label: "Subscribed",
              render: (v) => formatDate(v),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default AdminPage;
